import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { adminAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import { toast } from 'sonner';
import { DollarSign, Send } from 'lucide-react';

const AdminPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPendingPayouts();
      setPayouts(res.data?.pending_payouts || []);
    } catch (e) {
      toast.error('Error cargando pagos pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const process = async (p) => {
    if (!window.confirm(`Procesar pago de $${p.total_earnings} MXN a ${p.mobilitier_name}?`)) return;
    setProcessing(p.mobilitier_id);
    try {
      await adminAPI.processPayout(p.mobilitier_id, { booking_ids: p.booking_ids });
      toast.success(`Pago de $${p.total_earnings} MXN procesado`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error procesando pago';
      toast.error(detail);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  const totalPending = payouts.reduce((s, p) => s + (p.total_earnings || 0), 0);

  return (
    <Card data-testid="admin-payouts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          Pagos Pendientes ({payouts.length})
        </CardTitle>
        <p className="text-sm text-gray-600">Total pendiente: <span className="font-bold text-green-700">${totalPending.toLocaleString()} MXN</span></p>
      </CardHeader>
      <CardContent>
        {payouts.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No hay pagos pendientes en este momento.</p>
        ) : (
          <div className="space-y-3">
            {payouts.map((p) => (
              <div key={p.mobilitier_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-testid={`payout-row-${p.mobilitier_id}`}>
                <div className="flex-1">
                  <p className="font-medium">{p.mobilitier_name}</p>
                  <p className="text-sm text-gray-600">{p.mobilitier_email}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span>{p.bookings_count} reserva(s)</span>
                    <span className="text-gray-400">·</span>
                    <span>Comisión: ${p.platform_fee.toLocaleString()}</span>
                    <span className="text-gray-400">·</span>
                    {p.stripe_account_id ? (
                      <Badge className="bg-green-600 text-xs">Stripe OK</Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">Sin Stripe</Badge>
                    )}
                  </div>
                </div>
                <div className="text-right mr-4">
                  <p className="text-2xl font-bold text-green-700">${p.total_earnings.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">MXN a pagar</p>
                </div>
                <Button
                  onClick={() => process(p)}
                  disabled={!p.stripe_account_id || processing === p.mobilitier_id}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid={`process-payout-btn-${p.mobilitier_id}`}
                >
                  <Send className="w-4 h-4 mr-1" />
                  {processing === p.mobilitier_id ? 'Procesando...' : 'Pagar Ahora'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPayouts;
